/**
 * Route -- Renders children when the current hash path matches the pattern.
 *
 * Supports:
 *   - `component` prop: renders the component when matched
 *   - `children` as function: receives extracted params
 *   - `children` as ReactNode: renders directly when matched
 */
import React from "react";
import { useRouterContext, RouterContext } from "./Router";
import { matchRoute } from "./matchRoute";

export interface RouteProps {
  path: string;
  component?: React.ComponentType<Record<string, string>>;
  children?:
    | React.ReactNode
    | ((params: Record<string, string>) => React.ReactNode);
}

export function Route({ path, component: Component, children }: RouteProps) {
  const ctx = useRouterContext();
  const params = matchRoute(path, ctx.path);

  if (params === null) {
    return null;
  }

  const childCtx = { ...ctx, params };

  if (Component) {
    return (
      <RouterContext.Provider value={childCtx}>
        <Component {...params} />
      </RouterContext.Provider>
    );
  }

  if (typeof children === "function") {
    return (
      <RouterContext.Provider value={childCtx}>
        {children(params)}
      </RouterContext.Provider>
    );
  }

  return (
    <RouterContext.Provider value={childCtx}>
      {children}
    </RouterContext.Provider>
  );
}
